import { useEffect, useMemo, useState } from 'react'
import { Card, Empty, Select, Table, Tag, Typography } from '@arco-design/web-react'
import {
  getModelPricingSnapshotApi,
  describeEffectivePricing,
  type ModelPricingEntry,
} from '@/api/modelPricing'
import { getLogStatsApi } from '@/api/logs'
import { formatUsdAsDisplay, renderQuota } from '@/utils/quota'
import type { GroupRow } from '../ratioSettingUtils'

interface Props {
  groupRows: GroupRow[]
}

interface GroupRevenue {
  quota: number
  requests: number
  loading: boolean
}

const fmtPrice = (v: number | null) => (v === null ? '-' : formatUsdAsDisplay(v))

/**
 * 分组经济视图：把「模型生效价 × 分组倍率」摊开，并列出每个分组近 24h 的真实收入。
 * 这是回答「哪个分组在赚钱、某个模型卖给 VIP 是多少钱」的地方。
 */
export default function GroupEconomyTable({ groupRows }: Props) {
  const [entries, setEntries] = useState<Record<string, ModelPricingEntry>>({})
  const [model, setModel] = useState('')
  const [revenue, setRevenue] = useState<Record<string, GroupRevenue>>({})

  const usableGroups = useMemo(() => groupRows.filter((row) => row.key.trim()), [groupRows])

  useEffect(() => {
    getModelPricingSnapshotApi()
      .then((snap) => {
        const map: Record<string, ModelPricingEntry> = {}
        snap.entries.forEach((entry) => {
          if (
            entry.effective?.ModelRatio !== undefined ||
            entry.effective?.ModelPrice !== undefined
          ) {
            map[entry.model_name] = entry
          }
        })
        setEntries(map)
        setModel((prev) => prev || Object.keys(map).sort()[0] || '')
      })
      .catch(() => setEntries({}))
  }, [])

  // 每个分组近 24h 的收入（管理端日志统计，失败不阻塞）。
  // shortcut: 一次 tab 打开最多查 20 个分组，避免分组特别多时打出几十个请求；超出的分组不显示收入。
  useEffect(() => {
    const since = Math.floor(Date.now() / 1000) - 24 * 3600
    let cancelled = false
    usableGroups.slice(0, 20).forEach((row) => {
      setRevenue((prev) => ({
        ...prev,
        [row.key]: {
          quota: prev[row.key]?.quota ?? 0,
          requests: prev[row.key]?.requests ?? 0,
          loading: true,
        },
      }))
      getLogStatsApi({ type: 2, start_timestamp: since, group: row.key })
        .then((stat) => {
          if (cancelled) return
          setRevenue((prev) => ({
            ...prev,
            [row.key]: {
              quota: stat.quota_sum ?? 0,
              requests: stat.total_requests ?? 0,
              loading: false,
            },
          }))
        })
        .catch(() => {
          if (cancelled) return
          setRevenue((prev) => ({
            ...prev,
            [row.key]: { quota: 0, requests: 0, loading: false },
          }))
        })
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usableGroups.map((row) => row.key).join(',')])

  const entry = entries[model]
  const pricing = useMemo(
    () => (entry ? describeEffectivePricing(entry.effective, entry.configured) : null),
    [entry]
  )

  const modelOptions = useMemo(
    () =>
      Object.keys(entries)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [entries]
  )

  return (
    <Card
      title="分组经济视图"
      className="!rounded-2xl"
      extra={
        <Select
          showSearch
          value={model}
          onChange={setModel}
          options={modelOptions}
          placeholder="选择模型"
          style={{ width: 260 }}
        />
      }
    >
      {!Object.keys(entries).length ? (
        <Empty description="还没有可用的模型定价" />
      ) : (
        <div className="space-y-2">
          <Typography.Text className="text-xs text-[var(--console-text-muted)]">
            售价 = 该模型生效价 × 分组倍率（跨组倍率不含在内，实际扣费可能再打折/加价）。收入取近
            24h 消费日志。
          </Typography.Text>
          <Table
            borderCell
            pagination={false}
            rowKey="key"
            data={usableGroups}
            noDataElement="还没有任何分组"
            columns={[
              {
                title: '分组',
                width: 220,
                render: (_: unknown, row: GroupRow) => (
                  <div className="space-y-1">
                    <div className="font-medium">{row.displayName || row.key}</div>
                    <div className="flex items-center gap-1 text-xs text-[var(--console-text-faint)]">
                      <code>{row.key}</code>
                      {row.displayName.trim() ? (
                        <Tag color="green" size="small">
                          用户可选
                        </Tag>
                      ) : (
                        <Tag size="small">仅路由/计费</Tag>
                      )}
                    </div>
                  </div>
                ),
              },
              {
                title: '倍率',
                width: 90,
                render: (_: unknown, row: GroupRow) => (
                  <span className="tabular-nums">{row.ratio}</span>
                ),
              },
              {
                title: '输入价 / 1M',
                width: 130,
                render: (_: unknown, row: GroupRow) => (
                  <span className="tabular-nums">
                    {pricing?.inputPerMillion != null
                      ? fmtPrice(pricing.inputPerMillion * row.ratio)
                      : '-'}
                  </span>
                ),
              },
              {
                title: '输出价 / 1M',
                width: 130,
                render: (_: unknown, row: GroupRow) => (
                  <span className="tabular-nums">
                    {pricing?.outputPerMillion != null
                      ? fmtPrice(pricing.outputPerMillion * row.ratio)
                      : '-'}
                  </span>
                ),
              },
              {
                title: '近24h 收入',
                width: 140,
                render: (_: unknown, row: GroupRow) => {
                  const item = revenue[row.key]
                  if (!item) return <span className="text-xs text-slate-400">-</span>
                  return (
                    <div className="space-y-1 text-xs">
                      <div className="tabular-nums font-medium">
                        {item.loading && !item.quota ? '…' : renderQuota(item.quota)}
                      </div>
                      <div className="tabular-nums text-[var(--console-text-faint)]">
                        {item.requests} 次调用
                      </div>
                    </div>
                  )
                },
              },
            ]}
          />
          {pricing && pricing.defaultedKeys.length > 0 ? (
            <Typography.Text className="text-xs text-amber-600">
              注意：{model} 的 {pricing.defaultedKeys.join('、')}{' '}
              来自引擎默认值，上表售价已含这些默认。
            </Typography.Text>
          ) : null}
        </div>
      )}
    </Card>
  )
}
